import { useState, useEffect, useCallback } from 'react';

const cache = {
  followedBrands: { data: [], fetchedAt: 0 },
};

let inFlightFollowedBrands = null;

// TTL: 1 minute
const FOLLOWED_BRANDS_TTL_MS = 60000;

// Listeners: any mounted hook instance registers a forceUpdate here
const listeners = new Set();
function notifyListeners() {
  listeners.forEach((fn) => fn());
}

async function fetchFollowedBrandsData(force = false) {
  const now = Date.now();
  if (!force && cache.followedBrands.fetchedAt && now - cache.followedBrands.fetchedAt < FOLLOWED_BRANDS_TTL_MS) {
    return cache.followedBrands.data;
  }

  // Dedupe: if a request is already in-flight, wait for it
  if (inFlightFollowedBrands) {
    return inFlightFollowedBrands;
  }

  const promise = (async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/brand/followed`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        cache.followedBrands = { data, fetchedAt: Date.now() };
        return data;
      }
      throw new Error('Failed to fetch followed brands');
    } finally {
      inFlightFollowedBrands = null;
    }
  })();

  inFlightFollowedBrands = promise;
  return promise;
}

/**
 * Shared hook for followed brands data.
 * Uses module-level cache so data is shared across all mounted components.
 *
 * @returns {{ followedBrands: Array, loading: boolean, error: string|null, refresh: Function, lastUpdated: number }}
 */
/** Resets module cache (for tests and rare logout flows). */
export function resetFollowedBrandsCache() {
  cache.followedBrands = { data: [], fetchedAt: 0 };
  inFlightFollowedBrands = null;
}

/** Shared followed-brands fetch for dashboard omnibar indexing. */
export async function fetchFollowedBrandsForOmnibar(force = false) {
  return fetchFollowedBrandsData(force);
}

export default function useFollowedBrands() {
  const [followedBrands, setFollowedBrands] = useState(cache.followedBrands.data);
  const [loading, setLoading] = useState(!cache.followedBrands.fetchedAt);
  const [error, setError] = useState(null);

  // Sync local state from module cache
  const syncFromCache = useCallback(() => {
    setFollowedBrands(cache.followedBrands.data);
  }, []);

  // Register this instance as a listener
  useEffect(() => {
    listeners.add(syncFromCache);
    return () => {
      listeners.delete(syncFromCache);
    };
  }, [syncFromCache]);

  // Refresh function callable by consumers
  const refresh = useCallback(async (force = false) => {
    try {
      setError(null);
      const data = await fetchFollowedBrandsData(force);
      setFollowedBrands(data);
      notifyListeners();
    } catch (err) {
      setError(err.message || 'Failed to load followed brands.');
    }
  }, []);

  // Initial fetch on mount
  useEffect(() => {
    let cancelled = false;

    const loadData = async () => {
      if (cache.followedBrands.fetchedAt) {
        setFollowedBrands(cache.followedBrands.data);
        setLoading(false);

        // Refresh in background if stale
        const now = Date.now();
        if (now - cache.followedBrands.fetchedAt >= FOLLOWED_BRANDS_TTL_MS) {
          try {
            await refresh(false);
          } catch {
            // Ignore background refresh errors
          }
        }
        return;
      }

      setLoading(true);
      try {
        const data = await fetchFollowedBrandsData(false);
        if (!cancelled) {
          setFollowedBrands(data);
          notifyListeners();
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || 'Failed to load followed brands.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadData();

    return () => {
      cancelled = true;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    followedBrands,
    loading,
    error,
    refresh,
    lastUpdated: cache.followedBrands.fetchedAt,
  };
}
